export const termsOfService = `# Terms of Service

**Last Updated: April 13, 2025**

## 1. Introduction

Welcome to Collabish ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website, applications, and services (collectively, the "Service").

By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.

## 2. Eligibility

You must be at least 13 years of age (or the minimum legal age in your jurisdiction) to use our Service. By using our Service, you represent and warrant that you meet all eligibility requirements. If you are using the Service on behalf of an entity, organization, or company, you represent and warrant that you have the authority to bind that organization to these Terms.

## 3. Account Registration

### 3.1 Account Creation
To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.

### 3.2 Account Security
You are responsible for safeguarding your account credentials and for any activities or actions under your account. You agree to notify us immediately of any unauthorized use of your account. We cannot and will not be liable for any loss or damage arising from your failure to comply with this section.

### 3.3 TikTok Login
Our Service allows you to connect your account with TikTok. By connecting your account with TikTok, you agree to comply with TikTok's terms of service in addition to these Terms.

## 4. User Content

### 4.1 Content Responsibility
You are solely responsible for any content you post, upload, share, store, or otherwise provide through the Service ("User Content"). You represent and warrant that:
- You own or have the necessary rights to use and authorize us to use your User Content
- Your User Content does not violate the rights of any third party, including intellectual property rights and privacy rights
- Your User Content complies with these Terms and all applicable laws and regulations

### 4.2 Content License
By submitting User Content to the Service, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, prepare derivative works of, display, and perform the User Content in connection with the Service and our business operations.

### 4.3 Content Removal
We reserve the right, but not the obligation, to review, monitor, or remove User Content at our sole discretion for any reason or no reason, including User Content that we determine violates these Terms.

## 5. Prohibited Conduct

You agree not to engage in any of the following prohibited activities:
- Violating any applicable laws or regulations
- Infringing on the rights of others, including patent, trademark, trade secret, copyright, privacy, or other proprietary rights
- Using the Service to transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable
- Attempting to interfere with, compromise the system integrity or security, or decipher any transmissions to or from the servers running the Service
- Using any robot, spider, crawler, scraper, or other automated means to access the Service
- Bypassing measures we may use to prevent or restrict access to the Service
- Collecting or harvesting any personally identifiable information from the Service
- Impersonating another person or entity
- Engaging in any activity that could disable, overburden, damage, or impair the Service
- Using the Service for any commercial purpose, unless explicitly permitted by us

## 6. Intellectual Property Rights

### 6.1 Our Intellectual Property
The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive property of [Your Company Name] and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.

### 6.2 Feedback
If you provide feedback, ideas, or suggestions regarding our Service ("Feedback"), you grant us an unrestricted, perpetual, irrevocable, non-exclusive, fully-paid, royalty-free right to use the Feedback in any manner and for any purpose without compensation to you.

## 7. Third-Party Services and Content

### 7.1 Third-Party Services
The Service may contain links to third-party websites, applications, or services that are not owned or controlled by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.

### 7.2 TikTok Integration
Our Service integrates with TikTok's platform and services. Your use of such integrations is subject to TikTok's Terms of Service and other applicable policies. We are not responsible for any TikTok services, changes, or issues.

## 8. Payment Terms

### 8.1 Fees
We may collect fees for transactions on our site with the services we offer.

We may also offer certain features of the Service on a subscription basis subject to fees. You agree to pay all fees specified when you select such features. Fees are non-refundable except as required by law or as otherwise specified at the time of purchase.

### 8.2 Billing
If you provide us with your credit card information, you authorize us to charge your card for all fees associated with your account. If your payment cannot be completed, we may suspend or terminate your access to paid features.

## 9. Disclaimer of Warranties

THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT THE SERVICE WILL MEET YOUR REQUIREMENTS, BE AVAILABLE ON AN UNINTERRUPTED, SECURE, OR ERROR-FREE BASIS, OR BE ACCURATE, RELIABLE, COMPLETE, LEGAL, OR SAFE.

## 10. Limitation of Liability

IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
- YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE
- ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE
- ANY CONTENT OBTAINED FROM THE SERVICE
- UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT

OUR TOTAL LIABILITY FOR ANY CLAIMS UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU HAVE PAID TO US IN THE PAST TWELVE MONTHS.

## 11. Indemnification

You agree to defend, indemnify, and hold harmless [Your Company Name] and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.

## 12. Term and Termination

### 12.1 Term
These Terms shall remain in full force and effect while you use the Service.

### 12.2 Termination by You
You may terminate your account at any time by following the instructions on the Service or by contacting us.

### 12.3 Termination by Us
We may suspend or terminate your access to the Service, your account, or these Terms at any time for any reason without notice or liability. Upon termination, your right to use the Service will immediately cease.

### 12.4 Effect of Termination
Any provisions of these Terms that by their nature should survive termination shall remain in full force and effect.

## 13. Modifications to the Service

We reserve the right to modify or discontinue, temporarily or permanently, the Service (or any part thereof) with or without notice. You agree that we shall not be liable to you or to any third party for any modification, suspension, or discontinuance of the Service.

## 14. Changes to These Terms

We may revise these Terms from time to time at our sole discretion. When we make changes, we will update the "Last Updated" date at the top of these Terms. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes.

## 15. Governing Law and Dispute Resolution

These Terms shall be governed by and construed in accordance with the laws of [Your State/Country], without regard to its conflict of law provisions.

Any dispute arising from or relating to the subject matter of these Terms shall be finally settled by [arbitration/courts located in Your Jurisdiction]. 

## 16. Severability

If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.

## 17. Waiver

Our failure to enforce any right or provision of these Terms will not be considered a waiver of such right or provision. The waiver of any such right or provision will be effective only if in writing and signed by our authorized representative.

## 18. Entire Agreement

These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference herein, constitute the entire agreement between you and us regarding your use of the Service and supersede all prior and contemporaneous written or oral agreements between you and us.

## 19. Contact Information

If you have any questions about these Terms, please contact us at:

Collabish LLC

By using the Service, you acknowledge that you have read and understood these Terms and agree to be bound by them.`;
